import React, { useEffect, useRef, useState } from "react"

import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { getStageMoveDialogForStage } from "@/config/stageMoveDialogs"
import {
  type ActiveEngagements,
  type UpcomingShift,
  useFetchActiveEngagements,
} from "@/hooks/useActiveEngagements"
import { useFetchCompletionShiftConflicts } from "@/hooks/useMismatchedUpcomingShifts"
import { supabase } from "@/lib/supabase"

import { useUpdateApplicationStage } from "../hooks/useUpdateApplicationStage"
import { Application, FilterState } from "../types/application"
import KanbanColumnWithInfiniteScroll from "./KanbanColumnWithInfiniteScroll"
import RejectWarningDialog from "./applications/RejectWarningDialog"
import StageMoveDialog from "./applications/StageMoveDialog"
import { stagesConfig } from "./job-postings/KanbanStageConfig"
import SheriffTypeChangeWarningDialog from "./people/SheriffTypeChangeWarningDialog"

interface KanbanBoardProps {
  searchQuery?: string
  filters?: Partial<FilterState>
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ searchQuery = "", filters = {} }) => {
  const [draggingItem, setDraggingItem] = useState<Application | null>(null)
  const [items, setItems] = useState<Application[]>([])
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showStageMoveDialog, setShowStageMoveDialog] = useState(false)
  const [stageMoveDialogConfig, setStageMoveDialogConfig] =
    useState<ReturnType<typeof getStageMoveDialogForStage>>(null)
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [isSubmittingDialog, setIsSubmittingDialog] = useState(false)
  // Rejection with active engagements: hold the confirm payload (and the engagements to
  // display) until the warning is accepted
  const [pendingReject, setPendingReject] = useState<{
    data: {
      stage: string
      reason?: string
      note?: string
      salary?: number
    }
    engagements: ActiveEngagements
  }>(undefined)

  // Checked on demand for the dragged item when a rejection is confirmed
  const fetchActiveEngagements = useFetchActiveEngagements()
  const [upcomingShifts, setUpcomingShifts] = useState<UpcomingShift[] | null>(null)
  const fetchCompletionShiftConflicts = useFetchCompletionShiftConflicts()

  // Persist and restore horizontal scroll position of the kanban board
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const scrollStorageKey = "kanban-board-scrollLeft"

  // Restore saved scrollLeft on mount
  useEffect(() => {
    const saved = localStorage.getItem(scrollStorageKey)
    const el = scrollContainerRef.current
    if (!el) return
    if (saved) {
      const val = Number(saved)
      if (!Number.isNaN(val)) el.scrollLeft = val
    }
  }, [])

  // Save scrollLeft on scroll (rAF-throttled)
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        localStorage.setItem(scrollStorageKey, String(el.scrollLeft))
        ticking = false
      })
    }
    el.addEventListener("scroll", onScroll, { passive: true })
    return () => el.removeEventListener("scroll", onScroll as EventListener)
  }, [])

  // Use the mutation hook for updating application stage
  const { mutate: updateStage, isPending: isUpdating } = useUpdateApplicationStage()

  // Handle drag start
  const handleDragStart = (application: Application) => {
    setDraggingItem(application)
  }

  // Handle drag end
  const handleDragEnd = () => {
    // setDraggingItem(null);
  }

  // Handle drop in a column
  const contractIndex = stagesConfig.findIndex((s) => s.stage === "contract")
  const getLastAllowedPreContractStage = (allowed?: string[]) => {
    if (contractIndex <= 0) return undefined
    // If no allowed list provided, fall back to the immediate previous stage
    if (!allowed || allowed.length === 0) return stagesConfig[contractIndex - 1]
    for (let i = contractIndex - 1; i >= 0; i--) {
      const st = stagesConfig[i]
      if (allowed.includes(st.stage)) return st
    }
    return undefined
  }

  const handleDrop = (stage: string) => {
    if (draggingItem && draggingItem.stage !== stage) {
      // Check if the target stage is in the job posting's allowed stages
      const isStageAllowed =
        draggingItem.allowedStages &&
        Array.isArray(draggingItem.allowedStages) &&
        draggingItem.allowedStages.includes(stage)

      // Special case for rejected, hold, complete, and pending, which are always allowed
      const isSpecialStage =
        stage === "rejected" || stage === "hold" || stage === "complete" || stage === "pending"

      if (!isStageAllowed && !isSpecialStage) {
        toast.error("Invalid Stage", {
          description: `This application cannot be moved to ${stage} stage as it's not configured for this job posting.`,
        })
        return
      }

      // Salary dialog when moving into contract OR into the stage before contract if that stage auto progresses
      const isContract = stage === "contract"
      const lastPre = getLastAllowedPreContractStage(draggingItem.allowedStages)
      const isPreContractAuto = lastPre && lastPre.stage === stage ? lastPre.autoProgress : false
      const hasContractInAllowed =
        Array.isArray(draggingItem.allowedStages) && draggingItem.allowedStages.includes("contract")
      const contractHasSalary = !!draggingItem.contractSalary
      const conditions = [
        {
          when:
            (isContract && contractHasSalary) ||
            (isPreContractAuto && hasContractInAllowed && contractHasSalary),
          stage: "contract",
        },
        { when: isSpecialStage && stage === "hold", stage: "hold" },
        { when: isSpecialStage && stage === "rejected", stage: "rejected" },
      ]

      const found = conditions.find((c) => c.when)
      const dialogCfg = found ? getStageMoveDialogForStage(found.stage) : null

      if (dialogCfg) {
        setPendingStatus(stage)
        setStageMoveDialogConfig(dialogCfg)
        setShowStageMoveDialog(true)
      } else {
        setPendingStatus(stage)
        setShowConfirmation(true)
      }
    }
  }

  const submitStatusChange = () => {
    if (draggingItem && pendingStatus) {
      // Optimistically update UI
      const updatedItems = items.map((item) => {
        if (item.id === draggingItem.id) {
          return { ...item, status: pendingStatus }
        }
        return item
      })
      setItems(updatedItems)

      // Reset dialog state
      setPendingStatus(null)
      setShowConfirmation(false)

      // Update in Supabase using the mutation
      updateStage(
        {
          id: draggingItem.id,
          stage: pendingStatus,
        },
        {
          onSuccess: () => {
            toast.success("Status Updated", {
              description: `${draggingItem.name} moved to ${pendingStatus}`,
            })
          },
          onError: () => {
            // Revert UI on failure
            const revertedItems = items.map((item) => {
              if (item.id === draggingItem.id) {
                return draggingItem // Restore original state
              }
              return item
            })
            setItems(revertedItems)
          },
        },
      )
    }
  }

  // Handle confirmed status change
  const handleConfirmedStatusChange = async () => {
    if (draggingItem && pendingStatus === "complete") {
      try {
        const shifts = await fetchCompletionShiftConflicts(draggingItem.id)
        if (shifts.length > 0) {
          setShowConfirmation(false)
          setUpcomingShifts(shifts)
          return
        }
      } catch {
        toast.warning("Could not check upcoming shifts", {
          description:
            "Completing anyway. Upcoming shifts that don't match the job's sheriff type will be cancelled.",
        })
      }
    }

    submitStatusChange()
  }

  // Handle generic stage move dialog confirmation (e.g., rejection or hold)
  const handleStageMoveDialogConfirm = async (
    data: {
      stage: string
      reason?: string
      note?: string
      salary?: number
    },
    bypassEngagementCheck = false,
  ) => {
    if (draggingItem) {
      const targetStage = pendingStatus ?? data.stage

      // For rejections, warn if the person still has open timesheets / claimed shifts.
      if (!bypassEngagementCheck && targetStage === "rejected") {
        setIsSubmittingDialog(true)
        try {
          const engagements = await fetchActiveEngagements(draggingItem.personId)
          if (engagements.hasActiveEngagements) {
            setShowStageMoveDialog(false)
            setPendingReject({ data, engagements })
            return
          }
        } catch (_error) {
          toast.warning("Could not check active engagements", {
            description: "Proceeding with the rejection without the active work check.",
          })
        } finally {
          setIsSubmittingDialog(false)
        }
      }

      const isRejection = (pendingStatus ?? data.stage) === "rejected"
      const isHold = (pendingStatus ?? data.stage) === "hold"

      // Optimistically update UI
      const updatedItems = items.map((item) => {
        if (item.id === draggingItem.id) {
          return {
            ...item,
            status: pendingStatus ?? data.stage,
            stage_reason: data.reason as string,
            stage_note: data.note as string,
          }
        }
        return item
      })
      setItems(updatedItems)

      // Reset dialog state and close it
      setShowStageMoveDialog(false)
      setStageMoveDialogConfig(null)

      // Save salary first if applicable, then update stage
      try {
        setIsSubmittingDialog(true)
        if (typeof data.salary === "number") {
          const { error } = await supabase
            .from("applications")
            .update({ contract_salary: data.salary })
            .eq("id", draggingItem.id)
          if (error) throw error
          toast.success("Salary Saved", { description: "Contract salary updated" })
        }

        // Update in Supabase using the mutation
        updateStage(
          {
            id: draggingItem.id,
            stage: targetStage,
            stage_reason: data.reason,
            stage_note: data.note,
          },
          {
            onSuccess: () => {
              const stageLabel =
                stagesConfig.find((s) => s.stage === targetStage)?.label || targetStage
              if (isRejection) {
                toast.success("Application Rejected", {
                  description: `${draggingItem.name} was rejected`,
                })
              } else if (isHold) {
                toast.success("Application On Hold", {
                  description: `${draggingItem.name} was put on hold`,
                })
              } else {
                toast.success("Status Updated", {
                  description: `${draggingItem.name} moved to ${stageLabel}`,
                })
              }
            },
            onError: () => {
              // Revert UI on failure
              const revertedItems = items.map((item) => {
                if (item.id === draggingItem.id) {
                  return draggingItem // Restore original state
                }
                return item
              })
              setItems(revertedItems)
            },
          },
        )
      } catch (err) {
        const revertedItems = items.map((item) => {
          if (item.id === draggingItem.id) {
            return draggingItem // Restore original state
          }
          return item
        })
        setItems(revertedItems)
        toast.error("Action Failed", {
          description: err instanceof Error ? err.message : "Could not complete update",
        })
      } finally {
        setIsSubmittingDialog(false)
      }
    }
  }

  // Proceed with a rejection the admin confirmed despite active engagements
  const handleConfirmRejectAnyway = () => {
    if (pendingReject) {
      const { data } = pendingReject
      setPendingReject(undefined)
      void handleStageMoveDialogConfirm(data, true)
    }
  }

  // Handle status change from buttons
  const handleStatusChange = (id: string, status: string) => {
    const application = items.find((item) => item.id === id)
    if (application && application.status !== status) {
      // Check if the target stage is in the job posting's allowed stages
      const isStageAllowed =
        application.allowedStages &&
        Array.isArray(application.allowedStages) &&
        application.allowedStages.includes(status)

      // Special case for rejected and hold, which are always allowed
      const isSpecialStage = status === "rejected" || status === "hold" || status === "complete"

      if (!isStageAllowed && !isSpecialStage) {
        toast.error("Invalid Stage", {
          description: `This application cannot be moved to ${status} stage as it's not configured for this job posting.`,
        })
        return
      }

      setDraggingItem(application)
      const isContract = status === "contract"
      const lastPre = getLastAllowedPreContractStage(application.allowedStages)
      const isPreContractAuto = lastPre && lastPre.stage === status ? lastPre.autoProgress : false
      const hasContractInAllowed =
        Array.isArray(application.allowedStages) && application.allowedStages.includes("contract")
      const dialogCfg =
        getStageMoveDialogForStage(status) ||
        (isContract || (isPreContractAuto && hasContractInAllowed)
          ? getStageMoveDialogForStage("contract")
          : null)

      if (dialogCfg) {
        setPendingStatus(status)
        setStageMoveDialogConfig(dialogCfg)
        setShowStageMoveDialog(true)
      } else {
        setPendingStatus(status)
        setShowConfirmation(true)
      }
    }
  }

  return (
    <div className="kanban-board-container">
      <div className="kanban-board" ref={scrollContainerRef}>
        {stagesConfig
          .filter((stage) => !filters.stage || stage.stage === filters.stage)
          .map((stage) => (
            <KanbanColumnWithInfiniteScroll
              key={stage.id}
              title={stage.label}
              stageId={stage.stage}
              icon={<stage.icon size={18} color={stage.color} />}
              color={stage.color}
              searchQuery={searchQuery}
              filters={filters}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDrop={() => handleDrop(stage.stage)}
              onStatusChange={handleStatusChange}
            />
          ))}
      </div>

      {/* Regular status change confirmation dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to move {draggingItem?.name} to {pendingStatus}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingStatus(null)
                setShowConfirmation(false)
              }}
              disabled={isUpdating}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedStatusChange} disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Generic stage move dialog for any stage with a configured dialog */}
      {stageMoveDialogConfig && (
        <StageMoveDialog
          open={showStageMoveDialog}
          onOpenChange={setShowStageMoveDialog}
          config={stageMoveDialogConfig}
          applicantName={draggingItem ? draggingItem.name : ""}
          onConfirm={handleStageMoveDialogConfirm}
          isUpdating={isSubmittingDialog || isUpdating}
          initialSalary={draggingItem?.contractSalary ?? null}
        />
      )}

      {pendingReject && (
        <RejectWarningDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingReject(undefined)
          }}
          applicantName={draggingItem?.name ?? ""}
          engagements={pendingReject.engagements}
          onConfirm={handleConfirmRejectAnyway}
          isConfirming={isSubmittingDialog || isUpdating}
        />
      )}

      {upcomingShifts && (
        <SheriffTypeChangeWarningDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setUpcomingShifts(null)
              setPendingStatus(null)
            }
          }}
          trigger="application-complete"
          personName={draggingItem?.name ?? ""}
          upcomingShifts={upcomingShifts}
          onConfirm={() => {
            setUpcomingShifts(null)
            submitStatusChange()
          }}
          isConfirming={isUpdating}
        />
      )}
    </div>
  )
}

export default KanbanBoard
